import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Store, ShoppingBag, PackageCheck, Warehouse, Factory, ChefHat, Truck,
  UtensilsCrossed, BookOpen, Receipt, Coins, ClipboardCheck, Scale,
  Search, Radar, X, ArrowDownLeft, ArrowUpRight, Boxes, Tag,
} from "lucide-react";
import api from "../../lib/api";
import { Card, Badge, Spinner, EmptyState } from "../../lib/ui";
import { fmtRp, fmtWIB } from "../../lib/constants";

const COLORS = {
  violet: { text: "text-violet-600", bg: "bg-violet-50", ring: "ring-violet-200" },
  indigo: { text: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-200" },
  sky: { text: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-200" },
  blue: { text: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-200" },
  fuchsia: { text: "text-fuchsia-600", bg: "bg-fuchsia-50", ring: "ring-fuchsia-200" },
  amber: { text: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200" },
  orange: { text: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-200" },
  teal: { text: "text-teal-600", bg: "bg-teal-50", ring: "ring-teal-200" },
  emerald: { text: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  rose: { text: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200" },
  pink: { text: "text-pink-600", bg: "bg-pink-50", ring: "ring-pink-200" },
  yellow: { text: "text-yellow-700", bg: "bg-yellow-50", ring: "ring-yellow-200" },
  cyan: { text: "text-cyan-600", bg: "bg-cyan-50", ring: "ring-cyan-200" },
  slate: { text: "text-slate-600", bg: "bg-slate-100", ring: "ring-slate-200" },
};
const STAGES = {
  SUPPLIER: { label: "Supplier", icon: Store, color: "violet" },
  PURCHASE_ORDER: { label: "Purchase Order", icon: ShoppingBag, color: "indigo" },
  GOODS_RECEIVING: { label: "Penerimaan Barang", icon: PackageCheck, color: "sky" },
  CENTRAL_WAREHOUSE: { label: "Gudang Pusat", icon: Warehouse, color: "blue" },
  PRODUCTION: { label: "Produksi", icon: Factory, color: "fuchsia" },
  PREPARATION: { label: "Persiapan Harian", icon: ChefHat, color: "amber" },
  TRANSFER: { label: "Transfer ke Cabang", icon: Truck, color: "orange" },
  BRANCH_RECEIVING: { label: "Penerimaan Cabang", icon: PackageCheck, color: "teal" },
  READY_TO_COOK: { label: "Bahan Siap Masak", icon: UtensilsCrossed, color: "emerald" },
  RECIPE_CONSUMPTION: { label: "Konsumsi Resep", icon: BookOpen, color: "rose" },
  SALES: { label: "Penjualan", icon: Receipt, color: "pink" },
  COGS: { label: "HPP (COGS)", icon: Coins, color: "yellow" },
  STOCK_TAKE: { label: "Stok Opname", icon: ClipboardCheck, color: "cyan" },
  ADJUSTMENT: { label: "Penyesuaian & Limbah", icon: Scale, color: "slate" },
};
const st = (key) => STAGES[key] || STAGES.ADJUSTMENT;

function SummaryStat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-line bg-white px-3 py-2.5">
      <p className="text-[11px] font-bold text-ink-mute">{label}</p>
      <p className="font-heading text-base font-extrabold leading-tight text-ink">{value}</p>
      {sub && <p className="text-[10px] text-ink-mute">{sub}</p>}
    </div>
  );
}

function ChainPath({ chain }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2" data-testid="trace-chain-path">
      {chain.map((node, i) => {
        const s = st(node.key);
        const c = COLORS[s.color];
        const Icon = s.icon;
        return (
          <div key={node.key} className="flex items-center gap-2">
            <div className={`flex w-[112px] flex-col items-center gap-1 rounded-2xl border px-2 py-2.5 text-center ${node.active ? `${c.bg} ring-1 ${c.ring} border-transparent` : "border-line bg-white opacity-50"}`}
              data-testid={`trace-node-${node.key}`}>
              <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${node.active ? c.bg : "bg-app"} ${node.active ? c.text : "text-ink-mute"}`}><Icon size={16} /></span>
              <p className={`text-[10px] font-bold leading-tight ${node.active ? "text-ink" : "text-ink-mute"}`}>{s.label}</p>
              {node.active && node.event_count > 0 && <span className={`rounded-full px-1.5 text-[10px] font-bold ${c.text}`}>{node.event_count}×</span>}
            </div>
            {i < chain.length - 1 && <span className="text-ink-mute">›</span>}
          </div>
        );
      })}
    </div>
  );
}

function TraceEvent({ ev }) {
  const s = st(ev.stage);
  const c = COLORS[s.color];
  const Icon = s.icon;
  const isIn = ev.direction === "in";
  const link = ev.link || {};
  return (
    <div className="flex gap-3" data-testid={`trace-event-${ev.id}`}>
      <div className="flex flex-col items-center">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${c.bg} ${c.text}`}><Icon size={16} /></span>
        <span className="my-1 w-px flex-1 bg-line" />
      </div>
      <div className="flex-1 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c.bg} ${c.text}`}>{s.label}</span>
          <span className="text-xs font-semibold text-ink-soft">{ev.type_label_id}</span>
          <span className={`inline-flex items-center gap-0.5 text-sm font-extrabold ${isIn ? "text-emerald-600" : "text-danger-text"}`}>
            {isIn ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}{isIn ? "+" : "−"}{ev.quantity} {ev.unit || ""}
          </span>
          <span className="ml-auto text-[11px] text-ink-mute">{fmtWIB(ev.at)}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
          <span className="inline-flex items-center gap-1"><Boxes size={12} className="text-ink-mute" />{ev.location || (ev.scope === "central" ? "Gudang Pusat" : "Cabang")}</span>
          <span>Saldo: {ev.balance_before} → <b className="text-ink">{ev.balance_after}</b></span>
          {link.supplier_name && <span className="inline-flex items-center gap-1 text-violet-600"><Store size={12} /> {link.supplier_name}</span>}
          {link.doc_label && <span className="inline-flex items-center gap-1 font-mono text-primary"><Tag size={12} /> {link.doc_label}</span>}
          {link.to && <span>→ {link.to}</span>}
          {ev.total_cost > 0 && <span className="text-ink-mute">{fmtRp(ev.total_cost)}</span>}
          {ev.actor && <span className="text-ink-mute">oleh {ev.actor}</span>}
        </div>
        {ev.reason && <p className="mt-0.5 text-[11px] italic text-ink-mute">{ev.reason}</p>}
      </div>
    </div>
  );
}

export default function TraceabilityView({ initialProductId = "", onSelectProduct }) {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [showList, setShowList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/traceability/products").then(({ data }) => setProducts(data.rows || [])).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches(Array.isArray(data) ? data : data.rows || [])).catch(() => {});
  }, []);

  const loadTrace = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get(`/traceability/product/${pid}`, { params });
      setTrace(data);
    } catch { setTrace(null); } finally { setLoading(false); }
  }, [branchId, dateFrom, dateTo]);

  useEffect(() => {
    if (initialProductId && products.length) {
      const p = products.find((x) => x.id === initialProductId);
      if (p) { setSelected(p); }
    }
  }, [initialProductId, products]);

  useEffect(() => { if (selected) loadTrace(selected.id); }, [selected, loadTrace]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowList(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = products.filter((p) => p.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 40);

  return (
    <div data-testid="traceability-view">
      <div className="grid gap-3 sm:grid-cols-[2fr,1fr,auto,auto]">
        <div className="relative" ref={boxRef}>
          <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
            <Search size={16} className="text-ink-mute" />
            <input className="w-full bg-transparent text-sm outline-none" placeholder="Cari produk untuk ditelusuri…"
              value={selected ? selected.name : query}
              onChange={(e) => { setSelected(null); setTrace(null); setQuery(e.target.value); setShowList(true); }}
              onFocus={() => setShowList(true)} data-testid="trace-search" />
            {selected && <button onClick={() => { setSelected(null); setTrace(null); setQuery(""); }} data-testid="trace-clear"><X size={15} className="text-ink-mute" /></button>}
          </div>
          {showList && !selected && (
            <div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-line bg-white shadow-pop" data-testid="trace-product-list">
              {filtered.length === 0 ? <p className="px-3 py-3 text-sm text-ink-mute">Tidak ada produk</p>
                : filtered.map((p) => (
                  <button key={p.id} onClick={() => { setSelected(p); setShowList(false); onSelectProduct && onSelectProduct(p.id); }} data-testid={`trace-pick-${p.id}`}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-stone-50">
                    <span className="font-medium text-ink">{p.name} <span className="text-xs text-ink-mute">({p.unit})</span></span>
                    {p.has_history ? <Badge tone="success" className="!text-[10px]">Ada riwayat</Badge> : <span className="text-[10px] text-ink-mute">kosong</span>}
                  </button>
                ))}
            </div>
          )}
        </div>
        <select className="cf-input !py-2 !text-sm" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="trace-branch">
          <option value="">Semua Lokasi</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" className="cf-input !py-2 !text-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="trace-date-from" />
        <input type="date" className="cf-input !py-2 !text-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="trace-date-to" />
      </div>

      {!selected ? (
        <div className="mt-6"><EmptyState image={null} title="Pilih produk untuk melihat perjalanan rantai pasoknya" subtitle="Timeline lengkap: Supplier → PO → Penerimaan → Gudang → Produksi → Transfer → Cabang → Persiapan → Bahan Siap Masak → Konsumsi → Penjualan → HPP → Opname → Penyesuaian." /></div>
      ) : loading ? (
        <div className="flex h-48 items-center justify-center"><Spinner className="h-8 w-8" /></div>
      ) : !trace ? (
        <div className="mt-6"><EmptyState image={null} title="Gagal memuat" /></div>
      ) : (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="trace-summary">
            <SummaryStat label="Total Kejadian" value={trace.summary.event_count} />
            <SummaryStat label="Stok Pusat" value={`${trace.summary.central_stock} ${trace.product.unit || ""}`} />
            <SummaryStat label="Dibeli" value={`${trace.summary.purchased_qty}`} sub={fmtRp(trace.summary.purchased_value)} />
            <SummaryStat label="Terjual" value={`${trace.summary.sold_qty}`} />
            <SummaryStat label="HPP (COGS)" value={fmtRp(trace.summary.cogs_value)} />
            <SummaryStat label="Limbah" value={`${trace.summary.waste_qty}`} />
            <SummaryStat label="Biaya Rata-rata" value={fmtRp(trace.summary.average_cost)} />
          </div>

          {trace.summary.suppliers?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="trace-suppliers">
              <span className="text-xs font-bold text-ink-mute">Supplier:</span>
              {trace.summary.suppliers.map((s) => (
                <span key={s.supplier_id} className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                  <Store size={12} /> {s.supplier_name} · {s.receipts}× · {fmtRp(s.value)}
                </span>
              ))}
            </div>
          )}

          <Card>
            <h3 className="mb-3 font-heading text-base font-extrabold text-ink">Alur Rantai Pasok</h3>
            <ChainPath chain={trace.chain} />
          </Card>

          <Card>
            <div className="mb-3 flex items-center gap-2">
              <Radar size={18} className="text-primary" />
              <h3 className="font-heading text-base font-extrabold text-ink">Timeline Operasional</h3>
              <Badge tone="mute" className="ml-auto">{trace.timeline.length} kejadian</Badge>
            </div>
            {trace.timeline.length === 0 ? <EmptyState image={null} title="Belum ada pergerakan stok untuk produk ini" />
              : <div data-testid="trace-timeline">{[...trace.timeline].reverse().map((ev) => <TraceEvent key={ev.id} ev={ev} />)}</div>}
          </Card>
        </div>
      )}
    </div>
  );
}
